/**
 * LiteSlider — lightweight carousel replacement for jQuery + Slick
 * Supports: slidesToShow, responsive breakpoints, autoplay, touch/swipe
 * Pre-rendered track: if container's first child has class "lite-slider__track",
 * it is reused without DOM manipulation (eliminates CLS from node moves).
 * ~2KB minified vs jQuery+Slick's ~90KB
 */
(function () {
  'use strict';

  function LiteSlider(container, options) {
    this.container = typeof container === 'string' ? document.querySelector(container) : container;
    if (!this.container) return;

    this.defaults = {
      slidesToShow: 1,
      slidesToScroll: 1,
      autoplay: false,
      autoplaySpeed: 4000,
      infinite: false,
      responsive: []
    };

    this.options = Object.assign({}, this.defaults, options);
    this.currentIndex = 0;
    this.autoplayTimer = null;
    this.touchStartX = 0;
    this.touchEndX = 0;

    // Detect pre-rendered track to avoid DOM manipulation CLS.
    // If the container already has a .lite-slider__track child, read slides
    // from it directly; otherwise read from the container itself.
    var firstChild = this.container.firstElementChild;
    if (firstChild && firstChild.classList.contains('lite-slider__track')) {
      this.slides = Array.from(firstChild.children);
    } else {
      this.slides = Array.from(this.container.children);
    }

    this.totalSlides = this.slides.length;
    if (this.totalSlides === 0) return;
    this.container.liteSlider = this;

    this.init();
  }

  LiteSlider.prototype.getSettings = function () {
    // Prefer the container's own box (immune to a transiently widened layout viewport), fall back to innerWidth
    var w = Math.min(window.innerWidth, document.documentElement.clientWidth || window.innerWidth);
    var settings = {
      slidesToShow: this.options.slidesToShow,
      slidesToScroll: this.options.slidesToScroll
    };

    // Sort responsive breakpoints descending
    var breakpoints = (this.options.responsive || []).slice().sort(function (a, b) {
      return b.breakpoint - a.breakpoint;
    });

    for (var i = 0; i < breakpoints.length; i++) {
      if (w < breakpoints[i].breakpoint && breakpoints[i].settings) {
        settings.slidesToShow = breakpoints[i].settings.slidesToShow || settings.slidesToShow;
        settings.slidesToScroll = breakpoints[i].settings.slidesToScroll || settings.slidesToScroll;
      }
    }

    return settings;
  };

  LiteSlider.prototype.init = function () {
    var self = this;

    // Reuse pre-rendered track if present — no DOM node moves = no CLS.
    var firstChild = this.container.firstElementChild;
    if (firstChild && firstChild.classList.contains('lite-slider__track')) {
      this.track = firstChild;
    } else {
      this.track = document.createElement('div');
      this.track.className = 'lite-slider__track';
      while (this.container.firstChild) {
        this.track.appendChild(this.container.firstChild);
      }
      this.container.appendChild(this.track);
    }

    // Mark as initialized
    this.container.classList.add('lite-slider--initialized');

    // Apply styles
    this.container.style.overflow = 'hidden';
    // Containing block for absolutely-positioned children (e.g. .visually-hidden inside off-screen slides);
    // without it they escape the overflow clip and widen the layout viewport on mobile.
    if (window.getComputedStyle(this.container).position === 'static') this.container.style.position = 'relative';
    this.track.style.display = 'flex';
    this.track.style.transition = 'transform 0.25s ease';

    this.slides.forEach(function (slide) {
      slide.style.flexShrink = '0';
      slide.style.boxSizing = 'border-box';
    });

    this.updateLayout();

    // Touch events
    this.container.addEventListener('touchstart', function (e) {
      self.touchStartX = e.changedTouches[0].screenX;
      self.track.style.transition = 'none';
    }, { passive: true });

    this.container.addEventListener('touchend', function (e) {
      self.touchEndX = e.changedTouches[0].screenX;
      self.track.style.transition = 'transform 0.25s ease';
      self.handleSwipe();
    }, { passive: true });

    // Resize handler (debounced)
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        self.updateLayout();
        self.goTo(self.currentIndex, true);
      }, 150);
    });

    // Re-compute once everything has loaded and whenever the container box changes
    var relayout = function () { self.updateLayout(); self.goTo(self.currentIndex, true); };
    window.addEventListener('load', relayout);
    if (window.ResizeObserver) {
      var lastW = this.container.clientWidth;
      new ResizeObserver(function () {
        if (self.container.clientWidth !== lastW) { lastW = self.container.clientWidth; relayout(); }
      }).observe(this.container);
    }

    // Autoplay
    if (this.options.autoplay) {
      this.startAutoplay();
      this.container.addEventListener('mouseenter', function () { self.stopAutoplay(); });
      this.container.addEventListener('mouseleave', function () { self.startAutoplay(); });
    }
  };

  LiteSlider.prototype.updateLayout = function () {
    var settings = this.getSettings();
    var slideWidth = 100 / settings.slidesToShow;

    this.slides.forEach(function (slide) {
      slide.style.width = slideWidth + '%';
    });
    if (typeof this.options.onChange === 'function') {
      this.options.onChange(this, settings, Math.max(0, this.totalSlides - settings.slidesToShow));
    }
  };

  LiteSlider.prototype.goTo = function (index, instant) {
    var settings = this.getSettings();
    var maxIndex = Math.max(0, this.totalSlides - settings.slidesToShow);
    this.currentIndex = Math.max(0, Math.min(index, maxIndex));

    var slideWidth = 100 / settings.slidesToShow;
    var offset = -(this.currentIndex * slideWidth);

    if (instant) {
      this.track.style.transition = 'none';
    }
    this.track.style.transform = 'translateX(' + offset + '%)';
    if (typeof this.options.onChange === 'function') this.options.onChange(this, settings, maxIndex);

    if (instant) {
      var self = this;
      // Force reflow then restore transition
      this.track.offsetHeight;
      setTimeout(function () {
        self.track.style.transition = 'transform 0.25s ease';
      }, 20);
    }
  };

  LiteSlider.prototype.next = function () {
    var settings = this.getSettings();
    var maxIndex = Math.max(0, this.totalSlides - settings.slidesToShow);
    var nextIndex = this.currentIndex + settings.slidesToScroll;

    if (nextIndex > maxIndex) {
      if (this.options.infinite) {
        nextIndex = 0;
      } else {
        nextIndex = maxIndex;
      }
    }
    this.goTo(nextIndex);
  };

  LiteSlider.prototype.prev = function () {
    var settings = this.getSettings();
    var prevIndex = this.currentIndex - settings.slidesToScroll;

    if (prevIndex < 0) {
      if (this.options.infinite) {
        prevIndex = Math.max(0, this.totalSlides - settings.slidesToShow);
      } else {
        prevIndex = 0;
      }
    }
    this.goTo(prevIndex);
  };

  LiteSlider.prototype.handleSwipe = function () {
    var diff = this.touchStartX - this.touchEndX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        this.next();
      } else {
        this.prev();
      }
    } else {
      // Snap back
      this.goTo(this.currentIndex);
    }
  };

  LiteSlider.prototype.startAutoplay = function () {
    var self = this;
    this.stopAutoplay();
    this.autoplayTimer = setInterval(function () {
      var settings = self.getSettings();
      var maxIndex = Math.max(0, self.totalSlides - settings.slidesToShow);
      if (self.currentIndex >= maxIndex) {
        self.goTo(0);
      } else {
        self.next();
      }
    }, this.options.autoplaySpeed);
  };

  LiteSlider.prototype.stopAutoplay = function () {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  };

  // Expose globally
  window.LiteSlider = LiteSlider;
})();
